import sourceFile from "../data/edsy-outfitting.json";
import type { ModuleDef, Ship, ShipBuildSlot } from "./types.ts";
export interface SourceShip {
  name: string;
  fdname: string;
  slots: Record<string, number[]>;
  slotnames?: Record<string, string[]>;
  reserved?: Record<string, Record<string, Record<string, number>>>;
  stock: Record<string, number[]>;
  module: Record<string, { fdname?: string | null; name?: string }>;
  [key: string]: unknown;
}
export const edsyShips = sourceFile.ships as unknown as Record<string, SourceShip>;
export function edsyShipFor(ship: Ship): SourceShip | undefined {
  const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
  return edsyShips[ship.fdSymbol?.toLowerCase() ?? ""] ?? Object.values(edsyShips).find(source => normalize(source.name) === normalize(ship.name));
}
const sourceModules = sourceFile.stockModules as Record<string, { fdname: string | null; name: string | null }>;
const coreNames = ["PowerPlant", "MainEngines", "FrameShiftDrive", "LifeSupport", "PowerDistributor", "Radar", "FuelTank"];
const coreGroups = ["pp", "t", "fsd", "ls", "pd", "s", "ft"];

/** Overlay the pinned EDSY hull topology and stock fit, retaining known EDCC hull identities. */
export function applyPinnedShipData(ships: Ship[], modules: ModuleDef[]): void {
  const bySymbol = new Map(modules.map(module => [module.symbol.toLowerCase(), module]));
  for (const ship of ships) {
    const source = edsyShipFor(ship);
    if (!source) continue;
    const current = ship.buildSlots;
    const slots: ShipBuildSlot[] = [];
    const usedNames = new Set<string>();
    const add = (group: NonNullable<ShipBuildSlot["sourceGroup"]>, sourceIndex: number, size: number, previous: ShipBuildSlot | undefined, fallbackName: string, kind: ShipBuildSlot["kind"], index: number) => {
      let name = previous?.slotName ?? fallbackName;
      if (usedNames.has(name)) name = `${fallbackName}-${index}`;
      usedNames.add(name);
      let fdName = source.slotnames?.[group]?.[sourceIndex];
      if (!fdName) {
        if (group === "internal") fdName = `Slot${String(sourceIndex + 1).padStart(2, "0")}_Size${size}`;
        else if (group === "military") fdName = `Military${String(sourceIndex + 1).padStart(2, "0")}`;
        else if (group === "hardpoint") fdName = `${["Tiny", "Small", "Medium", "Large", "Huge"][size]}Hardpoint${source.slots.hardpoint!.slice(0, sourceIndex + 1).filter(value => value === size).length}`;
        else fdName = fallbackName;
      }
      slots.push({ slotName: name, kind, size, index, label: previous?.label ?? null,
        eligible: previous?.eligible ?? null, group: previous?.group ?? null,
        sourceGroup: group, sourceIndex, fdSlotName: fdName });
    };
    for (const [index, name] of coreNames.entries()) {
      add("component", index + 1, source.slots.component![index + 1]!, current.find(slot => slot.kind === "core" && slot.group === coreGroups[index]), name, "core", index);
    }
    for (const group of ["hardpoint", "utility"] as const) {
      for (const [index, size] of (source.slots[group] ?? []).entries()) add(group, index, size, current.find(slot => slot.kind === group && slot.index === index), group === "hardpoint" ? `Hardpoint${index + 1}` : `TinyHardpoint${index + 1}`, group, index);
    }
    let optionalIndex = 0;
    for (const group of ["military", "internal"] as const) {
      const old = current.filter(slot => slot.kind === "optional" && slot.slotName !== "PlanetaryApproachSuite" && slot.slotName.toLowerCase().startsWith("military") === (group === "military"));
      for (const [index, size] of (source.slots[group] ?? []).entries()) add(group, index, size, old[index], group === "military" ? `Military${String(index + 1).padStart(2, "0")}` : `Optional${index + 1}`, "optional", optionalIndex++);
    }
    const planetary = current.find(slot => slot.slotName === "PlanetaryApproachSuite");
    const armour = current.find(slot => slot.kind === "bulkhead");
    if (planetary) slots.push({ ...planetary, index: optionalIndex });
    if (armour) slots.push({ ...armour, sourceGroup: "component", sourceIndex: 0, fdSlotName: "Armour" });
    ship.buildSlots = slots;
    ship.slots.core = source.slots.component!.slice(1);
    ship.slots.hardpoints = [...(source.slots.hardpoint ?? []), ...(source.slots.utility ?? [])];
    ship.slots.optional = slots.filter(slot => slot.kind === "optional" && slot.slotName !== "PlanetaryApproachSuite").map(slot => slot.size);
    const properties = { hullCost: "cost", retailCost: "retail", speed: "topspd", boost: "bstspd", hullMass: "mass", baseArmour: "armour", baseShieldStrength: "shields", boostEnergy: "boostcost", hardness: "hardness", masslock: "masslock", heatCapacity: "heatcap", pitch: "pitch", roll: "roll", yaw: "yaw", reserveFuelCapacity: "fuelreserve" } as const;
    for (const [target, key] of Object.entries(properties)) {
      const value = source[key];
      if (typeof value === "number" && Number.isFinite(value)) Object.assign(ship, { [target]: value });
    }
    const defaults = slots.flatMap(slot => {
      if (slot.sourceGroup === undefined || slot.sourceIndex === undefined) return [];
      const id = source.stock[slot.sourceGroup]?.[slot.sourceIndex];
      const record = id ? source.module[String(id)] ?? sourceModules[String(id)] : undefined;
      const module = record?.fdname ? bySymbol.get(record.fdname.toLowerCase()) : undefined;
      return module ? [{ slotName: slot.slotName, kind: slot.kind, moduleSymbol: module.symbol, moduleName: module.name }] : [];
    });
    const planetaryModule = ship.defaultModules.find(module => module.slotName === "PlanetaryApproachSuite");
    if (planetaryModule) defaults.push(planetaryModule);
    ship.defaultModules = defaults;
  }
}
